/* eslint-disable eqeqeq */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { Component } from "react";
import PropTypes from "prop-types";
import MaskedInput from "react-text-mask";
import classNames from "classnames";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import {
	OfficerService,
	GenericService,
	JurisdictionFeeService,
	OrganizationService,
} from "@services";
import utils from "@utils";
import { PAY_RATE_TYPE } from "@constants";
import { ReactSelect, SelectBadges, Modal, Loader, Tooltip } from "../../../../../components";

import "./Position.scss";

const emptyPosition = {
	_isValid: false,
	_isRovingPatrol: false,
	_assetsRequired: false,
	_positionStartDate: "",
	_startTime: "",
	_endTime: "",
	_certificationRequired: false,
	_hasAssignedOfficers: false,
	_initialRate: "0",
	_isRankRequired: false,
	_isMaxRankRequired: false,
	_hasRankRestriction: false,
	Title: "",
	OfficersNeeded: "1",
	Rate: 0,
	Details: "",
	SupervisorPosition: false,
	RequestedOfficerRankId: "",
	MaxRequestedOfficerRankId: "",
	OfficerRankRequired: false,
	ApplyPTAEOCode: false,
	RovingPatrol: "",
	RequiredJurisdictionAssets: [],
	VehicleRequired: false,
	VehicleTypes: [],
	JobCertifications: [],
	DressTypeId: 1,
	DressTypeNote: "",
	OfficerRequirements: "",
	FlexJob: false,
	FlexJobMinHours: "",
	PayCodeId: "",
	PTAEOCodeId: "",
	vehicleChanged: false,
};

class PositionFormModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			position: { ...emptyPosition },
			certificationList: [],
			officerMaxRanks: [],
			officerMinRanks: [],
			dressTypes: [],
			vehicleTypes: [],
			additionalAssets: [],
			hasErrors: false,
			loadingSelectors: false,
			loadingAssets: false,
			wasSupervisor: false,
			selectedPositionGroup: "",
			hasRateChanged: false,
			positionRateModal: PAY_RATE_TYPE.HOURLY,
			agencyPayCodes: [],
			ptaeocodes: [],
		};
		this.officerRanks = [];
		this.timeMask = [/[0-1]/, /[0-9]/, ":", /[0-5]/, /[0-9]/, " ", /(A|P)/i, /M/i];
		this.dateMask = [/[0-1]/, /[0-9]/, "/", /[0-3]/, /[0-9]/, "/", /2/, /0/, /[2-9]/, /[0-9]/];
	}

	componentDidMount() {
		const { user } = this.props;
		this.getCertifications();
		this.getPositionsSelectOptions();
		this.getPTAEOCodes();
		if (user.isLEA()) this.getAdditionalAssets();
	}

	componentDidUpdate({ show: prevShow }) {
		const { show, positionIndex, positionGroup } = this.props;

		if (prevShow !== show && show && positionIndex !== undefined) this.setPositionDetails();
		else if (prevShow !== show && show && positionGroup !== undefined) this.setPositionGroup();
	}

	/** Sets the details of the position if editing an existing position */
	setPositionDetails = () => {
		const {
			Jobs,
			positionIndex,
			generalInfoStartDate,
			user: {
				Organization: { IsPayCodeMandatory },
			},
		} = this.props;

		const position = { ...Jobs[positionIndex] };
		position.vehicleChanged = false;
		let _positionRateModal = PAY_RATE_TYPE.HOURLY;
		position._startTime = utils.convertTime12to24(position._startTime);
		position._endTime = utils.convertTime12to24(position._endTime);
		const _payCodes = this.updatePayCodesList(
			generalInfoStartDate || position._positionStartDate,
			true,
			position._startTime,
		);
		let officerMinRanks = this.officerRanks.concat();
		let officerMaxRanks = this.officerRanks.concat();
		if (position.MaxRequestedOfficerRankId) {
			const selectedMaxRank = this.officerRanks.find(
				item => item.value === position.MaxRequestedOfficerRankId,
			);
			officerMinRanks = this.officerRanks.filter(
				item => selectedMaxRank.weight >= item.weight,
			);
		}
		if (position.RequestedOfficerRankId) {
			const selectedMinRank = this.officerRanks.find(
				item => item.value === position.RequestedOfficerRankId,
			);
			officerMaxRanks = this.officerRanks.filter(
				item => selectedMinRank.weight <= item.weight,
			);
		}

		if ((!position.Rate && !!position.PayCodeId) || IsPayCodeMandatory) {
			_positionRateModal = PAY_RATE_TYPE.PAY_CODE;
			const agencyPayCodesIds = _payCodes.map(p => p.value);
			if (!agencyPayCodesIds.includes(position.PayCodeId)) {
				position.PayCodeId = "";
			}
		}
		if (position.PTAEOCodeId != null && position.PTAEOCodeId !== "") {
			position.ApplyPTAEOCode = true;
			position.PTAEOCodeId = position.PTAEOCodeId.toString();
		}

		this.setState({
			position,
			officerMaxRanks,
			officerMinRanks,
			hasErrors: !position._isValid,
			wasSupervisor: position.SupervisorPosition,
			selectedPositionGroup: position.PositionGroupId || "",
			positionRateModal: _positionRateModal,
			agencyPayCodes: _payCodes,
		});
	};

	/** Sets the position group for the current position if one is provided via `this.props` */
	setPositionGroup = () => {
		const { positionGroup } = this.props;
		this.setState({ selectedPositionGroup: positionGroup });
	};

	getPTAEOCodes = () => {
		const { lang } = global;
		OrganizationService.getPTAEOCodes()
			.then(({ data }) => {
				const ptaeocodes = data.map(({ Id, Code }) => ({
					value: Id.toString(),
					label: Code,
				}));
				this.setState({ ptaeocodes });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading PTAEO codes"),
					timeOut: 1500,
				});
			});
	};

	/** Retrieves a list of officer certifications that can be required for a position */
	getCertifications = () => {
		const { lang } = global;
		OfficerService.getCertifications()
			.then(({ data }) => {
				const certificationList = data.map(
					({ OfficerCertificationTypeId, OfficerCertificationTypeName }) => ({
						value: OfficerCertificationTypeId,
						label: OfficerCertificationTypeName,
					}),
				);
				this.setState({ certificationList });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error retrieving certifications"),
					timeOut: 1500,
				});
			});
	};

	/** Retrieves lists for ranks, dress types, and vehicle types that can be selected per position */
	getPositionsSelectOptions = () => {
		this.setState({ loadingSelectors: true });
		Promise.all([
			OfficerService.getRanks(),
			GenericService.getDressTypes(),
			GenericService.getVehicleTypes(),
		])
			.then(
				([
					{ data: rawOfficerRanks },
					{ data: rawDressTypes },
					{ data: rawVehicleTypes },
				]) => {
					this.officerRanks = rawOfficerRanks.map(({ Value, Weight, Text }) => ({
						value: parseInt(Value, 10),
						weight: parseInt(Weight, 10),
						label: Text,
					}));
					const officerMaxRanks = rawOfficerRanks.map(({ Value, Weight, Text }) => ({
						value: parseInt(Value, 10),
						weight: parseInt(Weight, 10),
						label: Text,
					}));
					const officerMinRanks = rawOfficerRanks.map(({ Value, Weight, Text }) => ({
						value: parseInt(Value, 10),
						weight: parseInt(Weight, 10),
						label: Text,
					}));
					const dressTypes = rawDressTypes.map(({ Id: value, Name: label }) => ({
						value,
						label,
					}));
					const vehicleTypes = rawVehicleTypes.map(
						({ Id: value, Name: label, AuthorizationRequired }) => ({
							value,
							label,
							AuthorizationRequired,
						}),
					);
					this.setState({
						officerMaxRanks,
						officerMinRanks,
						dressTypes,
						vehicleTypes,
						loadingSelectors: false,
					});
				},
			)
			.catch(() => {
				utils.notify({
					type: "error",
					message: "Error loading position select options",
					timeOut: 1500,
				});
				this.setState({ loadingSelectors: false });
			});
	};

	/** Retrieves the list of jurisdiction assets available to be requested for a position */
	getAdditionalAssets = () => {
		this.setState({ loadingAssets: true });

		JurisdictionFeeService.getAll()
			.then(({ data: { JurisdictionAssets } }) => {
				const additionalAssets = JurisdictionAssets.map(
					({ Id: value, Description: label }) => ({ value, label }),
				);
				this.setState({ additionalAssets, loadingAssets: false });
			})
			.catch(() => {
				this.setState({ loadingAssets: false });
				utils.notify({
					type: "error",
					message: "Error getting additional assets",
					timeOut: 1500,
				});
			});
	};

	/**
	 * Adds a new certification requirement to the position
	 * @param {Event} e The HTML event handled by this method
	 */
	handleCertificationChange = e => {
		const { value } = e.target;
		const { certificationList } = this.state;
		if (value === "") {
			return;
		}
		const newCert = certificationList.find(item => item.value.toString() === value);
		if (newCert) {
			this.setState(prevState => ({
				position: {
					...prevState.position,
					JobCertifications: [
						...prevState.position.JobCertifications,
						{ value: newCert.value, label: newCert.label },
					],
				},
			}));
		}
	};

	/**
	 * Removes a certification requirement from the position
	 * @param {Number} certId The ID number for the certification
	 */
	handleRemoveCertification = certId => {
		this.setState(prevState => ({
			position: {
				...prevState.position,
				JobCertifications: prevState.position.JobCertifications.filter(
					item => item.value !== certId,
				),
			},
		}));
	};

	/** Sets the minimum number of officers needed to 2 when multiple officers are required for a position */
	resetOfficersNeeded = () => {
		const {
			position: { OfficersNeeded },
		} = this.state;
		// OnBlur : set OfficersNeeded value to initial state if OfficersNeeded is less than 2 or is NaN
		if (parseInt(OfficersNeeded, 10) < 1 || Number.isNaN(parseInt(OfficersNeeded, 10))) {
			this.setState(prevState => ({
				position: {
					...prevState.position,
					OfficersNeeded: "1",
				},
			}));
		}
	};

	/**
	 * Handles input changes inside the position modal. This includes radio buttons and checkboxes.
	 * @param {Event} event The HTML event handled by this method
	 */
	handleInputChange = ({ target: { name, value, type, checked } }) => {
		let newValue;
		const { position } = this.state;
		const { lang } = global;
		switch (type) {
			case "radio":
				// true if radio value is 'yes'
				newValue = value === "yes";
				break;
			case "checkbox":
				if (name !== "OfficerRankRequired" && name !== "ApplyPTAEOCode" && checked)
					newValue = [...position[name], { Id: parseInt(value, 10) }];
				else if (name === "OfficerRankRequired" || name === "ApplyPTAEOCode")
					newValue = checked;
				else newValue = position[name].filter(item => item.Id !== parseInt(value, 10));
				break;
			default:
				newValue = value;
				break;
		}
		// restrict inputs to numbers with 2 decimal points
		if ((name === "Rate" || name === "RovingPatrol") && !value.match(/^\d*\.?\d{0,2}$/g)) {
			newValue = position[name];
		}

		// restrict input to integers
		if (name === "OfficersNeeded" && !Number.isNaN(parseInt(value, 10))) {
			// reconvert number to string
			newValue = parseInt(value, 10).toString();
		}

		if (name === "VehicleRequired" && checked) {
			newValue = value === "yes";
			this.setState(prevState => ({
				...prevState,
				position: {
					...prevState.position,
					vehicleChanged: newValue,
				},
			}));
		}

		if (name === "_positionStartDate") {
			newValue = utils.isIOSDevice ? value.replace(/\//gi, "-") : value;
			const payCodesToSelectIds = this.updatePayCodesList(newValue).map(p => p.value);

			if (!!position.PayCodeId && !payCodesToSelectIds.includes(position.PayCodeId)) {
				utils.notify({
					type: "error",
					message: lang.t(
						"The selected pay code is outside the start date range, please change it to a valid one",
					),
					timeOut: 1500,
				});

				this.setState(prevState => ({
					...prevState,
					position: {
						...prevState.position,
						PayCodeId: "",
					},
				}));
			}
		}

		// Reset values of radio dependent fields
		const handleAdditionalChanges = () => {
			const updates = {};
			switch (name) {
				case "FlexJob":
					updates.FlexJobMinHours = "";
					break;
				case "_isRovingPatrol":
					updates.RovingPatrol = "";
					break;
				case "_assetsRequired":
					updates.RequiredJurisdictionAssets = [];
					break;
				case "VehicleRequired":
					updates.VehicleTypes = [];
					break;
				case "_certificationRequired":
					updates.JobCertifications = [];
					break;
				case "_hasRankRestriction":
					updates.RequestedOfficerRankId = "";
					updates.MaxRequestedOfficerRankId = "";
					updates.OfficerRankRequired = false;
					updates._isRankRequired = false;
					updates._isMaxRankRequired = false;
					this.setState({
						officerMaxRanks: this.officerRanks,
						officerMinRanks: this.officerRanks,
					});
					break;
				case "OfficerRankRequired":
					updates.MaxRequestedOfficerRankId = "";
					updates._isMaxRankRequired = false;
					this.setState({ officerMinRanks: this.officerRanks });
					break;
				case "_isRankRequired":
					updates.RequestedOfficerRankId = "";
					updates.OfficerRankRequired = false;
					this.setState({ officerMaxRanks: this.officerRanks });
					break;
				case "_isMaxRankRequired":
					updates.MaxRequestedOfficerRankId = "";
					this.setState({ officerMinRanks: this.officerRanks });
					break;
				default:
					break;
			}

			if (Object.keys(updates).length > 0) {
				this.setState(prevState => ({
					position: {
						...prevState.position,
						...updates,
					},
				}));
			}
		};

		this.setState(
			prevState => ({ position: { ...prevState.position, [name]: newValue } }),
			() => {
				if (newValue === false || name === "OfficerRankRequired") handleAdditionalChanges();
				else if (name === "Rate" && position._hasAssignedOfficers) {
					this.setState({
						hasRateChanged: Number(newValue) !== Number(position._initialRate),
					});
				}
			},
		);
	};

	handleSupervisorChange = ({ target: { value } }) => {
		const { Jobs } = this.props;
		const { position } = this.state;
		// If reporting position, find supervisor and set to same date
		const startDate = value
			? Jobs.find(j => j.SupervisorPosition === true && j.PositionGroupId === value)
					._positionStartDate
			: position._positionStartDate;

		this.setState({
			selectedPositionGroup: value,
			position: { ...position, _positionStartDate: startDate },
		});
	};

	/**
	 * Handles changes made using `ReactSelect` elements
	 * @param {Object} input The selected value
	 * @param {Event} event The HTML event handled by this method
	 */
	handleSelectChange = (input, { name }) => {
		if (name === "RequestedOfficerRankId") {
			const officerMaxRanks = this.officerRanks.filter(item => input.weight <= item.weight);
			this.setState({ officerMaxRanks });
		}
		if (name === "MaxRequestedOfficerRankId") {
			const officerMinRanks = this.officerRanks.filter(item => input.weight >= item.weight);
			this.setState({ officerMinRanks });
		}
		this.setState(prevState => ({ position: { ...prevState.position, [name]: input?.value } }));
	};

	handleEnterPress = ({ key }) => {
		if (key === "Enter") this.savePosition();
	};

	validateAndUpdatePositions = newJobs => {
		const { updateForm, validateSection, onHide } = this.props;

		validateSection("positions", newJobs.filter(item => item._isValid === false).length === 0);
		updateForm("positions", { Jobs: newJobs, _expanded: true });
		onHide();
	};

	/** Displays a confirmation modal prompting the user to confirm that the position includes a vehicle on the invoice */
	onVehicleRequiredConfirmation = newJobs => {
		const { lang } = global;
		const { confirmationModal } = this.props;

		confirmationModal.openModal({
			title: lang.t("Vehicle Required"),
			message: `${lang.t("Selecting a vehicle for this job will require vehicle invoicing_ Are you sure you want to include a vehicle on this job?")}`,
			confirmButton: `${lang.t("Yes, save a vehicle to this job")}`,
			onConfirm: () => {
				this.validateAndUpdatePositions(newJobs);
			},
		});
	};

	/** Validates the current section and saves latest position values to the HOC */
	savePosition = () => {
		const {
			position: {
				Title,
				Rate,
				_positionStartDate,
				_startTime,
				_endTime,
				_isRovingPatrol,
				SupervisorPosition,
				RovingPatrol,
				VehicleRequired,
				VehicleTypes,
				_assetsRequired,
				RequiredJurisdictionAssets,
				_certificationRequired,
				JobCertifications,
				_isRankRequired,
				_isMaxRankRequired,
				RequestedOfficerRankId,
				MaxRequestedOfficerRankId,
				FlexJob,
				FlexJobMinHours,
				PayCodeId,
				PTAEOCodeId,
				ApplyPTAEOCode,
				vehicleChanged,
			},
			wasSupervisor,
			selectedPositionGroup,
			positionRateModal,
		} = this.state;
		const { Jobs, isJobEvent, editingJob, positionIndex, RequireInvoicingForVehicles } =
			this.props;

		let _isValid = true;

		// Validation logic
		if (Title.trim() === "") _isValid = false;
		if (isJobEvent && _positionStartDate.trim() === "") _isValid = false;
		if (isJobEvent && _startTime.trim() === "") _isValid = false;
		else if (
			(utils.isIE || utils.isDesktopSafari) &&
			isJobEvent &&
			_startTime.replace(/_/g, "").length < 5
		)
			_isValid = false;
		if (isJobEvent && _endTime.trim() === "") _isValid = false;
		else if (
			(utils.isIE || utils.isDesktopSafari) &&
			isJobEvent &&
			_endTime.replace(/_/g, "").length < 5
		)
			_isValid = false;
		if (_isRovingPatrol && RovingPatrol.toString().trim() === "") _isValid = false;
		if (VehicleRequired && VehicleTypes.length === 0) _isValid = false;
		if (_assetsRequired && RequiredJurisdictionAssets.length === 0) _isValid = false;
		if (_certificationRequired && JobCertifications.length === 0) _isValid = false;
		if (_isRankRequired && RequestedOfficerRankId === "") _isValid = false;
		if (_isMaxRankRequired && MaxRequestedOfficerRankId === "") _isValid = false;
		if (ApplyPTAEOCode && (PTAEOCodeId === "" || PTAEOCodeId == null)) _isValid = false;

		switch (positionRateModal) {
			case PAY_RATE_TYPE.HOURLY:
				if (!utils.isPayRateValid(Rate)) _isValid = false;
				this.setState({ PayCodeId: "" });
				break;
			case PAY_RATE_TYPE.PAY_CODE:
				if (!PayCodeId || PayCodeId.toString().trim() === "") _isValid = false;
				this.setState({ Rate: "" });
				break;
			default:
				break;
		}

		// Calculate the job duration
		let jobDuration = this.timeToHours(_endTime) - this.timeToHours(_startTime);
		// If the number is negative this means the End Time is 12 AM which equates to 0 so we must add 24 to compensate
		if (jobDuration < 0) jobDuration += 24;

		// For flex jobs if the min hours value is ("" || 0) or if the value is greater than the total hours that can be worked on the job, return false
		if (
			FlexJob &&
			(FlexJobMinHours === "" ||
				parseFloat(FlexJobMinHours) === 0 ||
				parseFloat(FlexJobMinHours) > jobDuration ||
				parseFloat(FlexJobMinHours) >= 24)
		)
			_isValid = false;

		let PositionGroupId;
		if (SupervisorPosition && !selectedPositionGroup) {
			// If this is a newly set supervisor position, give it a new PositionGroupId
			PositionGroupId = `positionGroup-${new Date().getTime()}`;
		} else if (wasSupervisor && !SupervisorPosition) {
			// If this position had its supervisor status removed, remove its PositionGroupId
			PositionGroupId = undefined;
		} else {
			// In all other scenarios, use the existing position group ID for the position
			// For no selection, set it to undefined
			PositionGroupId = selectedPositionGroup !== "" ? selectedPositionGroup : undefined;
		}

		this.setState(
			prevState => ({
				position: {
					...prevState.position,
					_isValid,
					Rate: positionRateModal === PAY_RATE_TYPE.PAY_CODE ? 0 : Rate,
					PayCodeId: positionRateModal === PAY_RATE_TYPE.HOURLY ? "" : PayCodeId,
					PTAEOCodeId: ApplyPTAEOCode ? PTAEOCodeId : "",
					PositionGroupId,
					positionRate: positionRateModal,
				},
			}),
			() => {
				const { position } = this.state;
				let newJobs = [...Jobs];
				// If this position had its supervisor status removed, disassociate other positions from the old positionGroup
				if (wasSupervisor && !SupervisorPosition) {
					newJobs = newJobs.map(job => {
						if (job.PositionGroupId === selectedPositionGroup)
							return { ...job, PositionGroupId: undefined };
						return job;
					});
				}
				newJobs[positionIndex === undefined ? Jobs.length : positionIndex] = position;
				const positionGroupMap = {};
				if (editingJob) {
					// Existing jobs with position groups return a GUID, which makes it difficult for us to sort with the current method
					// To work around this, we create a map of existing position group IDs and rename them to something easier to sort
					newJobs.forEach((job, index) => {
						if (job.PositionGroupId && !positionGroupMap[job.PositionGroupId]) {
							positionGroupMap[job.PositionGroupId] = `positionGroup-${index}`;
						}
					});
					newJobs = newJobs.map(job => ({
						...job,
						PositionGroupId: positionGroupMap[job.PositionGroupId],
					}));
				}
				newJobs.sort((a, b) => {
					const aPositionGroup = (
						a.PositionGroupId || new Date().getTime().toString()
					).replace("positionGroup-", "");
					const bPositionGroup = (
						b.PositionGroupId || new Date().getTime().toString()
					).replace("positionGroup-", "");
					// In order of priority, sort by PositionGroupId, then supervisor status
					return (
						aPositionGroup - bPositionGroup ||
						b.SupervisorPosition - a.SupervisorPosition
					);
				});
				if (editingJob) {
					// Again, if editing a job, we want to preserve the original data from the API. So, map back to the original
					// Position group IDs provided by the API
					const positionGroupEntries = Object.entries(positionGroupMap);
					newJobs = newJobs.map(job => {
						if (
							positionGroupEntries.some(([, value]) => value === job.PositionGroupId)
						) {
							const oldId = positionGroupEntries.find(
								([, value]) => value === job.PositionGroupId,
							)[0];
							return { ...job, PositionGroupId: oldId };
						}
						return job;
					});
				}

				if (_isValid) {
					if (
						VehicleRequired &&
						VehicleTypes.length > 0 &&
						RequireInvoicingForVehicles &&
						vehicleChanged
					) {
						this.onVehicleRequiredConfirmation(newJobs);
					} else {
						this.validateAndUpdatePositions(newJobs);
					}
				}
			},
		);
	};

	/** Navigates the user to the agency's settings page */
	onRedirect = () => {
		const { lang } = global;
		const { push, confirmationModal } = this.props;

		confirmationModal.openModal({
			title: lang.t("Redirect Confirmation"),
			message: lang.t("Navigating to agency settings will lose any unsaved changes_"),
			confirmationButton: lang.t("Continue"),
			onConfirm: () => {
				push("/agency-account#assets");
			},
		});
	};

	/** Updates the minimum hours for a flex position
	 * @param {Event} event The HTML event handled by this method
	 */
	onFlexHoursInputChange = event => {
		const {
			target: { name, value },
		} = event;
		if (!value.match(/^\d*\.?\d{0,2}$/g)) return;
		this.setState(prevState => ({
			position: {
				...prevState.position,
				[name]: value,
			},
		}));
	};

	/**
	 * Converts a given time into a decimal formatted value for calculating job duration
	 * @param {String} time
	 * @returns {Number} a decimal representation of how much time has passed from 12:00 AM to the time provided
	 */
	timeToHours = time => {
		const string = time.split(":");
		const decimal = parseInt((string[1] / 6) * 10, 10);
		return parseFloat(`${parseInt(string[0], 10)}.${decimal < 10 ? "0" : ""}${decimal}`);
	};

	onExited = () => {
		this.setState({
			position: { ...emptyPosition },
			hasErrors: false,
			wasSupervisor: false,
			selectedPositionGroup: "",
			hasRateChanged: false,
		});
	};

	handleChange = e => {
		const {
			target: { name, value },
		} = e;
		let newValue = value;

		if (name === "positionRateModal") {
			newValue = parseInt(value, 10);
			this.setState(prevState => ({
				position: {
					...prevState.position,
					PayCodeId: null,
					Rate: "",
					positionRate: value,
				},
			}));
		}

		this.setState({
			[name]: newValue,
		});
	};

	// eslint-disable-next-line default-param-last
	updatePayCodesList(_startDate, _setState = true, startTime) {
		const { rawAgencyPayCodes } = this.props;
		const {
			position: { _startTime },
		} = this.state;
		const _payCodes = utils.filterPayCodesByDateAndStatus(
			_startDate,
			rawAgencyPayCodes,
			startTime || _startTime,
		);

		if (_setState) this.setState({ agencyPayCodes: _payCodes });
		return _payCodes;
	}

	render() {
		const { lang } = global;
		const {
			positionIndex,
			Jobs,
			user,
			user: {
				Organization: { PayCodesEnabled, IsPayCodeMandatory, UsePTAEOCode },
			},
			show,
			onHide,
			isJobEvent,
			minStartDate,
			clients,
			clientId,
			OnlyAuthorizedClientsAreAllowedToUseVehicles,
		} = this.props;

		const {
			position,
			certificationList,
			officerMinRanks,
			officerMaxRanks,
			vehicleTypes,
			dressTypes,
			additionalAssets,
			loadingSelectors,
			loadingAssets,
			hasErrors,
			wasSupervisor,
			ptaeocodes,
			selectedPositionGroup,
			hasRateChanged,
			positionRateModal,
			agencyPayCodes,
		} = this.state;

		const { PayCodeId, PTAEOCodeId, Rate } = position;

		const selectedClient = clients.find(c => c.value === clientId);

		const departmentOptionsDisabled =
			OnlyAuthorizedClientsAreAllowedToUseVehicles &&
			!selectedClient?.AuthorizedToUseDepartmentVehicles;

		// Find a list of all current supervisors. If the current position is a supervisor, filter out their name from the list
		let supervisorList = Jobs.filter(({ SupervisorPosition }) => SupervisorPosition).map(
			supervisor => ({ value: supervisor.PositionGroupId, label: supervisor.Title }),
		);
		if (wasSupervisor)
			supervisorList = supervisorList.filter(
				({ value }) => value !== position.PositionGroupId,
			);

		let jobDuration =
			this.timeToHours(position._endTime) - this.timeToHours(position._startTime);
		if (jobDuration < 0) jobDuration += 24;
		return (
			<Modal
				show={show}
				onHide={() => onHide()}
				onExited={this.onExited}
			>
				<Modal.Header closeButton>
					<Modal.Title>
						{positionIndex === undefined ? "New Position" : "Edit Position"}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className={classNames("job-position", { "invalid-fields": hasErrors })}>
					<Loader active={loadingSelectors || loadingAssets} />
					<div className="row group-box">
						<div className="row">
							<div
								className={`col-xs-12 col-sm-6 form-group${position.Title === "" ? " invalid-field" : ""}`}
							>
								<label htmlFor="Title">
									{lang.t("Position Name")}
									<span className="required-mark" />
								</label>
								<input
									type="text"
									className="form-control"
									name="Title"
									id="positionTitle"
									data-test-id="position-modal__title"
									value={position.Title}
									onChange={this.handleInputChange}
									onKeyPress={this.handleEnterPress}
								/>
							</div>
							<div>
								<label htmlFor="officersNeeded">{lang.t("Officers Needed")}</label>
								<span className="required-mark" />
							</div>
							<div
								className={classNames("col-xs-12 col-sm-6 form-group", {
									"has-error":
										parseInt(position.OfficersNeeded, 10) < 1 ||
										Number.isNaN(parseInt(position.OfficersNeeded, 10)),
								})}
							>
								<input
									type="tel"
									min="1"
									className="form-control"
									value={position.OfficersNeeded}
									id="OfficersNeeded"
									data-test-id="position-modal__officers-needed"
									name="OfficersNeeded"
									onChange={this.handleInputChange}
									onKeyPress={this.handleEnterPress}
									onBlur={this.resetOfficersNeeded}
								/>
							</div>
						</div>
						<If condition={UsePTAEOCode}>
							<div className="row">
								<div
									key="apply-PTAEO-code"
									className="ptaeo-code-checkbox checkbox"
								>
									<label htmlFor="apply-PTAEO-code">
										<input
											id="apply-PTAEO-code"
											data-test-id="position-modal__apply-ptaeo-code"
											type="checkbox"
											name="ApplyPTAEOCode"
											checked={position.ApplyPTAEOCode}
											onChange={this.handleInputChange}
										/>
										Apply PTAEO Code
									</label>
								</div>
							</div>
						</If>
						<If condition={position.ApplyPTAEOCode}>
							<div className="row">
								<div
									className={classNames("col-xs-12 col-sm-12", {
										"has-error": !PTAEOCodeId,
									})}
								>
									<label htmlFor="position-PTAEOCode-id">
										{lang.t("PTAEO Code")}
										<span className="required-mark" />
									</label>
								</div>
								<div
									className={classNames("col-xs-12 col-sm-6 form-group", {
										"has-error": !PTAEOCodeId,
									})}
								>
									<ReactSelect
										id="position-PTAEOCode-id"
										dataTestId="position-modal__ptaeo-code"
										value={PTAEOCodeId}
										name="PTAEOCodeId"
										onChange={this.handleSelectChange}
										onMenuClose={this.validateSection}
										options={ptaeocodes}
										isLoading={false}
										className="react-select__control"
									/>
								</div>
							</div>
						</If>
						<div className="row">
							<If condition={PayCodesEnabled && !IsPayCodeMandatory}>
								<div
									className="form-group no-margin-formgroup"
									data-toggle="buttons"
								>
									<div className="col-xs-4 col-sm-3 radio">
										<label htmlFor="positionRateModal-hourly">
											<input
												type="radio"
												value={PAY_RATE_TYPE.HOURLY}
												name="positionRateModal"
												id="positionRateModal-hourly"
												data-test-id="position-modal__hourly-rate"
												onChange={e => this.handleChange(e)}
												checked={positionRateModal == PAY_RATE_TYPE.HOURLY}
											/>
											{lang.t("Hourly Rate")}
										</label>
									</div>
									<div className="col-xs-8 col-sm-3 radio">
										<label htmlFor="positionRateModal-payCode">
											<input
												type="radio"
												value={PAY_RATE_TYPE.PAY_CODE}
												name="positionRateModal"
												id="positionRateModal-payCode"
												data-test-id="position-modal__rate-pay-code"
												onChange={e => this.handleChange(e)}
												checked={
													positionRateModal == PAY_RATE_TYPE.PAY_CODE
												}
											/>
											{lang.t("Pay Code")}
										</label>
									</div>
								</div>
							</If>
							<Choose>
								<When condition={positionRateModal == PAY_RATE_TYPE.HOURLY}>
									<div className="row col-xs-12 col-sm-12">
										<div
											className={classNames("col-xs-12 col-sm-6 form-group", {
												"has-error":
													parseInt(Rate, 10) < 1 ||
													Number.isNaN(parseInt(Rate, 10)),
											})}
										>
											<label
												htmlFor="Rate"
												className="no-margin-label"
											>
												{`${lang.t("Rate")} (USD) ${lang.t("per hour")}`}
												<span className="required-mark" />
											</label>
											<input
												type="tel"
												min="1"
												className="form-control"
												value={Rate}
												id="PositionRate"
												name="Rate"
												onChange={this.handleInputChange}
												onKeyPress={this.handleEnterPress}
												data-test-id="jobs__position-rate"
											/>
										</div>
										<If condition={hasRateChanged}>
											<div className="col-xs-12 alert alert-warning">
												{lang.t(
													"Please be aware that you are changing the rate for a position that already has officers assigned to it_",
												)}
											</div>
										</If>
									</div>
								</When>
								<Otherwise>
									<div
										className={classNames("col-xs-12 col-sm-12", {
											"has-error": !PayCodeId,
										})}
									>
										<label htmlFor="position-payCode-id">
											{lang.t("Pay Code")}
											<span className="required-mark" />
										</label>
									</div>
									<div
										className={classNames("col-xs-12 col-sm-6 form-group", {
											"has-error": !PayCodeId,
										})}
									>
										<ReactSelect
											id="position-payCode-id"
											dataTestId="position-modal__pay-code-id"
											value={PayCodeId}
											name="PayCodeId"
											onChange={this.handleSelectChange}
											onMenuClose={this.validateSection}
											options={agencyPayCodes}
											isLoading={false}
											className="react-select__control"
										/>
									</div>
								</Otherwise>
							</Choose>
						</div>
					</div>
					<If condition={isJobEvent}>
						<div className="row group-box">
							<div
								className={`col-xs-12 form-group${position._positionStartDate === "" ? " invalid-field" : ""}`}
							>
								<label htmlFor="positionStartDate">{lang.t("Date")}</label>
								<Choose>
									<When condition={utils.isDesktopSafari || utils.isIE}>
										<MaskedInput
											name="_positionStartDate"
											id="positionStartDate"
											data-test-id="position-modal__start-date"
											type="text"
											mask={this.dateMask}
											className="form-control"
											placeholder="mm/dd/yyyy"
											disabled={selectedPositionGroup}
											value={position._positionStartDate}
											onChange={this.handleInputChange}
											onKeyPress={this.handleEnterPress}
										/>
									</When>
									<Otherwise>
										<input
											name="_positionStartDate"
											id="positionStartDate"
											data-test-id="position-modal__start-date"
											className="form-control"
											type="date"
											min={minStartDate}
											value={position._positionStartDate}
											disabled={selectedPositionGroup && !wasSupervisor}
											onChange={this.handleInputChange}
											onKeyPress={this.handleEnterPress}
										/>
									</Otherwise>
								</Choose>
							</div>
							<div
								className={`col-xs-6 form-group${position._startTime === "" ? " invalid-field" : ""}`}
							>
								<label htmlFor="positionStartTime">
									{lang.t("Start Time")}
									<span className="required-mark" />
								</label>
								<Choose>
									<When condition={utils.isDesktopSafari || utils.isiE}>
										<MaskedInput
											name="_startTime"
											id="positionStartTime"
											data-test-id="position-modal__start-time"
											type="text"
											mask={this.timeMask}
											className="form-control"
											placeholder="--:-- --"
											value={position._startTime}
											onChange={this.handleInputChange}
											onKeyPress={this.handleEnterPress}
										/>
									</When>
									<Otherwise>
										<input
											name="_startTime"
											id="positionStartTime"
											data-test-id="position-modal__start-time"
											className="form-control"
											type="time"
											value={position._startTime}
											onChange={this.handleInputChange}
											onKeyPress={this.handleEnterPress}
										/>
									</Otherwise>
								</Choose>
							</div>
							<div
								className={`col-xs-6 form-group${position._endTime === "" ? " invalid-field" : ""}`}
							>
								<label htmlFor="positionEndTime">
									{lang.t("End Time")}
									<span className="required-mark" />
								</label>
								<Choose>
									<When condition={utils.isDesktopSafari || utils.isIE}>
										<MaskedInput
											name="_endTime"
											id="positionEndTime"
											data-test-id="position-modal__end-time"
											type="text"
											mask={this.timeMask}
											className="form-control"
											placeholder="--:-- --"
											value={position._endTime}
											onChange={this.handleInputChange}
											onKeyPress={this.handleEnterPress}
										/>
									</When>
									<Otherwise>
										<input
											name="_endTime"
											id="positionEndTime"
											data-test-id="position-modal__end-time"
											className="form-control"
											type="time"
											value={position._endTime}
											onChange={this.handleInputChange}
											onKeyPress={this.handleEnterPress}
										/>
									</Otherwise>
								</Choose>
							</div>
							<div className="col-xs-12">
								{lang.t("Is this a flexible shift_")}
								<Tooltip
									content={lang.t(
										"Flex jobs allow the officer to work any time within the specified hours, and do not conflict with scheduled jobs",
									)}
								>
									<i className="rkf rkf-question-solid job-form-tooltip tooltip-icon" />
								</Tooltip>
							</div>
							<div className="col-xs-4 col-sm-2 radio">
								<label htmlFor="flexibleNo">
									<input
										type="radio"
										name="FlexJob"
										id="flexibleNo"
										data-test-id="position-modal__flexible-no"
										value="no"
										checked={position.FlexJob === false}
										onChange={this.handleInputChange}
									/>
									{lang.t("No")}
								</label>
							</div>
							<div className="col-xs-4 col-sm-2 radio">
								<label htmlFor="flexibleYes">
									<input
										type="radio"
										name="FlexJob"
										id="flexibleYes"
										value="yes"
										checked={position.FlexJob === true}
										onChange={this.handleInputChange}
										data-test-id="jobs__flexible-yes"
									/>
									{lang.t("Yes")}
								</label>
							</div>
							<If condition={position.FlexJob}>
								<div className="col-xs-12">
									<div
										className={classNames("flex-hours", {
											"has-error":
												position.FlexJob &&
												(position.FlexJobMinHours === "" ||
													parseFloat(position.FlexJobMinHours) === 0 ||
													parseFloat(position.FlexJobMinHours) >
														jobDuration ||
													parseFloat(position.FlexJobMinHours) >= 24),
										})}
									>
										<span>{lang.t("Minimum")}</span>
										<input
											type="tel"
											name="FlexJobMinHours"
											id="FlexJobMinHours"
											data-test-id="jobs__flex-min-hours"
											value={position.FlexJobMinHours}
											className="form-control"
											onChange={e => this.onFlexHoursInputChange(e)}
											onKeyPress={e => {
												if (e.key === "Enter")
													this.onFlexHoursInputChange(e);
											}}
										/>
										<span>{lang.t("Hours Required")}</span>
									</div>
								</div>
							</If>
						</div>
					</If>
					<div className="row form-group">
						<label htmlFor="Details">
							{lang.t("Are there special instructions for this position_")}{" "}
						</label>
						<textarea
							type="text"
							className="form-control"
							value={position.Details}
							name="Details"
							id="PositionDetails"
							data-test-id="position-modal__details"
							onChange={this.handleInputChange}
							onKeyPress={this.handleEnterPress}
							placeholder={lang.t(
								"Please provide general traffic control at the corner of the street and parking garage_",
							)}
							rows={4}
						/>
					</div>
					<div className="row group-box">
						<If condition={!selectedPositionGroup || wasSupervisor}>
							<div>{lang.t("Is this a supervisory position")}</div>
							<div className="form-group no-margin-formgroup">
								<div className="col-xs-4 col-sm-2 radio">
									<label htmlFor="supervisorPosition_no">
										<input
											type="radio"
											value="no"
											name="SupervisorPosition"
											id="supervisorPosition_no"
											data-test-id="position-modal__supervisor-no"
											onChange={this.handleInputChange}
											checked={position.SupervisorPosition === false}
										/>
										{lang.t("No")}
									</label>
								</div>
								<div className="col-xs-8 col-sm-10 radio">
									<label htmlFor="supervisorPosition_yes">
										<input
											type="radio"
											value="yes"
											name="SupervisorPosition"
											id="supervisorPosition_yes"
											data-test-id="position-modal__supervisor-yes"
											onChange={this.handleInputChange}
											checked={position.SupervisorPosition === true}
										/>
										{lang.t("Yes")}
									</label>
								</div>
							</div>
						</If>
						<If condition={!position.SupervisorPosition && supervisorList.length > 0}>
							<div className="form-group">
								<label htmlFor="selectSupervisor">
									{lang.t("Select Supervisor")}
								</label>
								<select
									name="selectSupervisor"
									id="selectSupervisor"
									data-test-id="position-modal__select-supervisor"
									className="form-control"
									onChange={this.handleSupervisorChange}
									value={selectedPositionGroup}
								>
									<option value="">--</option>
									<For
										each="supervisor"
										of={supervisorList}
									>
										<option
											key={supervisor.value}
											value={supervisor.value}
										>
											{supervisor.label}
										</option>
									</For>
								</select>
							</div>
						</If>
					</div>
					<div className="row group-box">
						<div>{lang.t("Does this position have rank restrictions_")}</div>
						<div className="form-group no-margin-formgroup row">
							<div className="col-xs-4 col-sm-2 radio">
								<label htmlFor="hasRank_no">
									<input
										type="radio"
										value="no"
										name="_hasRankRestriction"
										id="hasRankRestriction_no"
										data-test-id="position-modal__has-rank-restriction-no"
										onChange={this.handleInputChange}
										checked={position._hasRankRestriction === false}
									/>
									{lang.t("No")}
								</label>
							</div>
							<div className="col-xs-8 col-sm-10 radio">
								<label htmlFor="hasRank_yes">
									<input
										type="radio"
										value="yes"
										name="_hasRankRestriction"
										id="hasRankRestriction_yes"
										data-test-id="position-modal__has-rank-restriction-yes"
										onChange={this.handleInputChange}
										checked={position._hasRankRestriction === true}
									/>
									{lang.t("Yes")}
								</label>
							</div>
						</div>
						<If condition={position._hasRankRestriction}>
							<div>{lang.t("Does this position require a minimum rank_")}</div>
							<div className="form-group no-margin-formgroup row">
								<div className="col-xs-4 col-sm-2 radio">
									<label htmlFor="isRankRequired_no">
										<input
											type="radio"
											value="no"
											name="_isRankRequired"
											id="isRankRequired_no"
											data-test-id="position-modal__is-rank-required-no"
											onChange={this.handleInputChange}
											checked={position._isRankRequired === false}
										/>
										{lang.t("No")}
									</label>
								</div>
								<div className="col-xs-8 col-sm-10 radio">
									<label htmlFor="isRankRequired_yes">
										<input
											type="radio"
											value="yes"
											name="_isRankRequired"
											id="isRankRequired_yes"
											data-test-id="position-modal__is-rank-required-yes"
											onChange={this.handleInputChange}
											checked={position._isRankRequired === true}
										/>
										{lang.t("Yes")}
									</label>
								</div>
							</div>
							<If condition={position._isRankRequired}>
								<div
									className={classNames("form-group", {
										"has-error":
											position._isRankRequired &&
											position.RequestedOfficerRankId === "" &&
											position.OfficerRankRequired === true,
									})}
								>
									<ReactSelect
										id="requested-officer-rank"
										name="RequestedOfficerRankId"
										data-test-id="position-modal__requested-officer-rank"
										value={position.RequestedOfficerRankId}
										onChange={this.handleSelectChange}
										options={officerMinRanks}
										isLoading={false}
										isClearable
									/>
								</div>
								<div className="form-group">
									<div className="checkbox">
										<label htmlFor="OfficerRankRequired">
											<input
												type="checkbox"
												name="OfficerRankRequired"
												id="OfficerRankRequired"
												data-test-id="position-modal__officer-rank-required"
												checked={position.OfficerRankRequired === true}
												onChange={this.handleInputChange}
											/>
											{lang.t("Restrict to this rank only")}
										</label>
									</div>
								</div>
							</If>
							<If condition={position.OfficerRankRequired === false}>
								<div>{lang.t("Does this position require a maximum rank_")}</div>
								<div className="form-group no-margin-formgroup row">
									<div className="col-xs-4 col-sm-2 radio">
										<label htmlFor="isMaxRankRequired_no">
											<input
												type="radio"
												value="no"
												name="_isMaxRankRequired"
												id="isMaxRankRequired_no"
												data-test-id="position-modal__is-max-rank-required-no"
												onChange={this.handleInputChange}
												checked={position._isMaxRankRequired === false}
											/>
											{lang.t("No")}
										</label>
									</div>
									<div className="col-xs-8 col-sm-10 radio">
										<label htmlFor="isMaxRankRequired_yes">
											<input
												type="radio"
												value="yes"
												name="_isMaxRankRequired"
												id="isMaxRankRequired_yes"
												data-test-id="position-modal__is-max-rank-required-yes"
												onChange={this.handleInputChange}
												checked={position._isMaxRankRequired === true}
											/>
											{lang.t("Yes")}
										</label>
									</div>
								</div>
								<If condition={position._isMaxRankRequired}>
									<div
										className={classNames("form-group", {
											"has-error":
												position._isMaxRankRequired &&
												position.MaxRequestedOfficerRankId === "" &&
												position.OfficerRankRequired === true,
										})}
									>
										<ReactSelect
											id="requested-officer-max-rank"
											name="MaxRequestedOfficerRankId"
											data-test-id="position-modal__requested-officer-max-rank"
											value={position.MaxRequestedOfficerRankId}
											onChange={this.handleSelectChange}
											options={officerMaxRanks}
											isLoading={false}
											isClearable
										/>
									</div>
								</If>
							</If>
						</If>
					</div>
					<div className="row group-box">
						<div>{lang.t("Is this a roving patrol position_")}</div>
						<div className="col-xs-4 col-sm-2 radio">
							<label htmlFor="positionRovingPatrol_no">
								<input
									type="radio"
									value="no"
									name="_isRovingPatrol"
									id="positionRovingPatrol_no"
									data-test-id="position-modal__roving-patrol-no"
									onChange={this.handleInputChange}
									checked={position._isRovingPatrol === false}
								/>
								{lang.t("No")}
							</label>
						</div>
						<div className="col-xs-8 col-sm-10 radio">
							<label htmlFor="positionRovingPatrol_yes">
								<input
									type="radio"
									value="yes"
									name="_isRovingPatrol"
									id="positionRovingPatrol_yes"
									data-test-id="position-modal__roving-patrol-yes"
									onChange={this.handleInputChange}
									checked={position._isRovingPatrol === true}
								/>
								{lang.t("Yes")}
							</label>
						</div>
						<If condition={position._isRovingPatrol}>
							<div
								className={classNames("form-group", {
									"has-error":
										parseInt(position.RovingPatrol, 10) < 1 ||
										Number.isNaN(parseInt(position.RovingPatrol, 10)),
								})}
							>
								<div className="col-xs-12">
									<label htmlFor="RovingPatrol">
										{lang.t("Set Radius")}
										<span className="required-mark" />
										<em className="roving-patrol-help-text">{`(${lang.t("Roving Patrol Jobs require you to set radius in miles")})`}</em>
									</label>
								</div>
								<div className="col-xs-4">
									<input
										type="tel"
										min="1"
										className="form-control"
										value={position.RovingPatrol}
										name="RovingPatrol"
										id="RovingPatrol"
										data-test-id="position-modal__roving-patrol-radius"
										onChange={this.handleInputChange}
										onKeyPress={this.handleEnterPress}
									/>
								</div>
							</div>
						</If>
					</div>
					<If condition={user.isLEA()}>
						<div className="row group-box">
							<div>
								{lang.t("Is additional equipment required for this position_")}
							</div>
							<div className="form-group no-margin-formgroup">
								<div className="col-xs-4 col-sm-2 radio">
									<label htmlFor="assetsRequired_no">
										<input
											type="radio"
											value="no"
											name="_assetsRequired"
											id="assetsRequired_no"
											data-test-id="position-modal__assets-required-no"
											onChange={this.handleInputChange}
											checked={position._assetsRequired === false}
										/>
										{lang.t("No")}
									</label>
								</div>
								<div className="col-xs-8 col-sm-10 radio">
									<label htmlFor="assetsRequired_yes">
										<input
											type="radio"
											value="yes"
											name="_assetsRequired"
											id="assetsRequired_yes"
											data-test-id="position-modal__assets-required-yes"
											onChange={this.handleInputChange}
											checked={position._assetsRequired === true}
										/>
										{lang.t("Yes")}
									</label>
								</div>
							</div>
							<If condition={position._assetsRequired}>
								<Choose>
									<When condition={additionalAssets.length > 0}>
										<div
											className={classNames(
												"form-group no-margin-formgroup",
												{
													"has-error":
														position.RequiredJurisdictionAssets
															.length === 0,
												},
											)}
										>
											<label htmlFor="jurisdictionAsset_">
												{lang.t(
													"Please select the assets/equipment required",
												)}
											</label>
											<For
												each="item"
												of={additionalAssets}
											>
												<div
													key={`jurisdictionAsset_${item.value}`}
													className="checkbox"
												>
													<label
														htmlFor={`jurisdictionAsset_${item.value}`}
													>
														<input
															id={`jurisdictionAsset_${item.value}`}
															data-test-id="position-modal__jurisdiction-asset"
															type="checkbox"
															name="RequiredJurisdictionAssets"
															value={item.value}
															checked={
																position.RequiredJurisdictionAssets.map(
																	a => a.Id,
																).indexOf(item.value) !== -1
															}
															onChange={this.handleInputChange}
														/>
														{item.label}
													</label>
												</div>
											</For>
										</div>
									</When>
									<Otherwise>
										<div className="define-asset-container">
											{`${lang.t("No assets defined_")} ${
												user.IsStaffAdmin
													? `${lang.t("Please add your available equipment/assets in your Agency Settings")}`
													: `${lang.t("Contact your agency_s administrator to make necessary assets available")}`
											}`}
											<If condition={user.IsStaffAdmin}>
												<button
													type="button"
													className="btn btn-link"
													onClick={this.onRedirect}
													data-test-id="position-modal__go-to-agency-settings"
												>
													{lang.t("dynamic.Go to", {
														place: lang.t("Agency Settings"),
													})}
												</button>
											</If>
										</div>
									</Otherwise>
								</Choose>
							</If>
						</div>
					</If>
					<div className="row group-box">
						<div>
							{lang.t("Is a vehicle required for this position_")}
							<Tooltip
								content={lang.t(
									"If this job requires that the officer use a vehicle, please indicate the type of vehicle required_",
								)}
							>
								<i className="rkf m rkf-question-solid job-form-tooltip tooltip-icon" />
							</Tooltip>
						</div>
						<div className="form-group no-margin-formgroup">
							<div className="col-xs-4 col-sm-2 radio">
								<label htmlFor="vehicleRequired_no">
									<input
										type="radio"
										name="VehicleRequired"
										id="vehicleRequired_no"
										data-test-id="position-modal__vehicle-required-no"
										value="no"
										onChange={this.handleInputChange}
										checked={position.VehicleRequired === false}
									/>
									{lang.t("No")}
								</label>
							</div>
							<div className="col-xs-8 col-sm-10 radio">
								<label htmlFor="vehicleRequired_yes">
									<input
										type="radio"
										name="VehicleRequired"
										id="vehicleRequired_yes"
										data-test-id="position-modal__vehicle-required-yes"
										value="yes"
										onChange={this.handleInputChange}
										checked={position.VehicleRequired === true}
									/>
									{lang.t("Yes")}
								</label>
							</div>
						</div>
						<If condition={position.VehicleRequired}>
							<div
								className={classNames(
									"form-group no-margin-formgroup multiple-checkbox",
									{ "has-error-custom": position.VehicleTypes.length === 0 },
								)}
							>
								<label htmlFor="vehicleTypes">
									{lang.t("Please select all the vehicles types that apply")}
									<span className="required-mark" />
								</label>
								<For
									each="item"
									of={vehicleTypes}
								>
									<div
										key={`vehicleType_${item.value}`}
										className="checkbox"
									>
										<label htmlFor={`vehicleType_${item.value}`}>
											<input
												id={`vehicleType_${item.value}`}
												type="checkbox"
												name="VehicleTypes"
												data-test-id="position-modal__vehicle-type"
												value={item.value}
												checked={
													position.VehicleTypes.map(v => v.Id).indexOf(
														item.value,
													) !== -1
												}
												onChange={this.handleInputChange}
												disabled={
													departmentOptionsDisabled &&
													item.AuthorizationRequired
												}
											/>
											<If
												condition={
													departmentOptionsDisabled &&
													item.AuthorizationRequired
												}
											>
												<Tooltip
													content={lang.t(
														"This client is not authorized to use department vehicles, please contact your admin if this is incorrect",
													)}
												>
													<label className="disabled">{item.label}</label>
												</Tooltip>
											</If>
											<If
												condition={
													!departmentOptionsDisabled ||
													!item.AuthorizationRequired
												}
											>
												<label htmlFor={`vehicleType_${item.value}`}>
													{item.label}
												</label>
											</If>
										</label>
									</div>
								</For>
							</div>
						</If>
					</div>
					<div className="row group-box">
						<div>{lang.t("Are certifications required for this position_")}</div>
						<div className="form-group no-margin-formgroup">
							<div className="col-xs-4 col-sm-2 radio">
								<label htmlFor="certificationRequired_no">
									<input
										type="radio"
										name="_certificationRequired"
										id="certificationRequired_no"
										data-test-id="position-modal__certification-required-no"
										value="no"
										onChange={this.handleInputChange}
										checked={position._certificationRequired === false}
									/>
									{lang.t("No")}
								</label>
							</div>
							<div className="col-xs-8 col-sm-10 radio">
								<label htmlFor="certificationRequired_yes">
									<input
										type="radio"
										name="_certificationRequired"
										id="certificationRequired_yes"
										data-test-id="position-modal__certification-required-yes"
										value="yes"
										onChange={this.handleInputChange}
										checked={position._certificationRequired === true}
									/>
									{lang.t("Yes")}
								</label>
							</div>
						</div>
						<If condition={position._certificationRequired}>
							<div
								className={classNames("form-group no-margin-formgroup", {
									"has-error":
										position._certificationRequired &&
										position.JobCertifications.length === 0,
								})}
							>
								<SelectBadges
									id="select-badges-postions"
									dataTestId="position-modal__certification-required"
									labelText={lang.t("Select the certifications required")}
									defaultOption={lang.t("Select a certification")}
									optionList={certificationList}
									storedOptionList={position.JobCertifications}
									handleOnChange={this.handleCertificationChange}
									handleOnDelete={this.handleRemoveCertification}
								/>
							</div>
						</If>
					</div>
					<div className="row group-box">
						<div className="form-group">
							<label htmlFor="DressTypeId">{lang.t("Dress Type")}</label>
							<ReactSelect
								id="dress-type-id"
								dataTestId="position-modal__dress-type-id"
								value={position.DressTypeId}
								name="DressTypeId"
								options={dressTypes}
								onChange={this.handleSelectChange}
								isClearable
							/>
						</div>
						<div className="form-group">
							<textarea
								type="text"
								className="form-control"
								value={position.DressTypeNote}
								name="DressTypeNote"
								id="DressTypeNote"
								data-test-id="position-modal__dress-type-note"
								onChange={this.handleInputChange}
								onKeyPress={this.handleEnterPress}
								placeholder="Add notes here"
								rows={4}
							/>
						</div>
					</div>
					<div className="row form-group">
						<label htmlFor="OfficerRequirements">{lang.t("Other Requirements")}</label>
						<textarea
							type="text"
							className="form-control"
							value={position.OfficerRequirements}
							name="OfficerRequirements"
							id="OfficerRequirements"
							data-test-id="position-modal__officer-requirements"
							onChange={this.handleInputChange}
							onKeyPress={this.handleEnterPress}
							placeholder="Do you have other requirements?"
							rows={4}
						/>
					</div>
					<div className="row text-center continue-button-container">
						<button
							type="button"
							id="save-position"
							onClick={this.savePosition}
							className="btn btn-primary"
							data-test-id="jobs__save-position"
						>
							{lang.t("Save")}
						</button>
						<button
							type="button"
							id="cancel-edit-position"
							data-test-id="jobs__cancel-edit-position"
							onClick={() => onHide(positionIndex)}
							className="btn btn-primary"
						>
							{lang.t("Cancel")}
						</button>
					</div>
				</Modal.Body>
			</Modal>
		);
	}
}

PositionFormModal.propTypes = {
	isJobEvent: PropTypes.bool.isRequired,
	editingJob: PropTypes.bool,
	show: PropTypes.bool,
	onHide: PropTypes.func,
	updateForm: PropTypes.func,
	validateSection: PropTypes.func,
	push: PropTypes.func,
	Jobs: PropTypes.array,
	minStartDate: PropTypes.string,
	confirmationModal: PropTypes.object,
	user: PropTypes.object,
	positionIndex: PropTypes.number,
	positionGroup: PropTypes.string,
	rawAgencyPayCodes: PropTypes.array,
	generalInfoStartDate: PropTypes.string,
	clients: PropTypes.array,
	clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	OnlyAuthorizedClientsAreAllowedToUseVehicles: PropTypes.bool,
	RequireInvoicingForVehicles: PropTypes.bool,
};

PositionFormModal.defaultProps = {
	editingJob: false,
	show: false,
	onHide: () => {},
	updateForm: () => {},
	validateSection: () => {},
	push: () => {},
	Jobs: [],
	minStartDate: "",
	user: {},
	confirmationModal: undefined,
	positionIndex: undefined,
	positionGroup: undefined,
	rawAgencyPayCodes: [],
	generalInfoStartDate: "",
	clients: [],
	OnlyAuthorizedClientsAreAllowedToUseVehicles: false,
	RequireInvoicingForVehicles: false,
};

export default withLDConsumer()(PositionFormModal);
